<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import ModalBox from '@/components/modals/ModalBox.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'
import { APP_EVENTS, emitAppEvent } from '@/Utils/appEventBus'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import _ from 'lodash'
import { ref, onMounted, onBeforeUnmount, computed, getCurrentInstance, watch } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import IconBase from '../icon/IconBase.vue'
import IconReplace from '../icon/IconReplace.vue'
import IconDownload from '../icon/IconDownload.vue'
import IconCamera from '../icon/IconCamera.vue'
import IconMoveTo from '../icon/IconMoveTo.vue'
import IconBin from '../icon/IconBin.vue'
import IconGear from '../icon/IconGear.vue'
import IconPlus from '../icon/IconPlus.vue'
import IconTag from '../icon/IconTag.vue'
import IconChevronDown from '../icon/IconChevronDown.vue'
import DropDownMenu from '../Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue'
import FormButton from '../Atoms/FormButton.vue'
import { bytes } from '../../filtersNew'
import VODTags from '../ui/VODTags.vue'
import ReplaceVodModal from '@/components/modals/ReplaceVodModal.vue'
import AddPreRecordStreamModal from "@/components/modals/AddPreRecordStreamModal.vue"
import MoveVod from '../../components/VOD/MoveVod.vue'
import StreamService from '@/services/StreamService'
import PlaylistService from "@/services/Playlistservice";
import { track, EVENTS } from '@/lib/tracking'

const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance();
const root = instance.proxy.$root;

const { isVisible: isMoveVodVisible } = useLegacyModalBridge('move-vod-modal')
const route = useRoute()
const router = useRouter()

const vodTags = ref(null)

const selectedVodFile = ref(null)
const showMoveDialog = ref(false)
const selectedVideoId = ref(null)
const isDownloading = ref(false)
const isVodBeingMoved = ref(false)
const isVodRemovable = ref(false)
const vodLinkedPlayout = ref(null)

const props = defineProps({
  vodFile: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id')
    }
  },
  vodFolder: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id')
    }
  },
  queryString: {
    type: String,
  },
  playbackUrl: {
    type: String,
    required: false
  }
})

const vodStreams = computed(() => store.getters['Streams/vodStreams'])
const vodSubfolders = computed(() => store.getters['Streams/vodSubfolders'] || [])
const canDownloadSource = computed(() => store.getters['User/canDownloadSource'])

const activeTeam = computed(() => store.state.User.activatedTeam);
const memberPermission = computed(() => store.state.User.memberPermission);

const canDeleteVideoFile = computed(() => !activeTeam.value || (activeTeam.value && memberPermission.value && memberPermission.value.hostingDelete))

const ownerEmail = computed(() => store.state.User.email)


const isLinkedPlayoutTooltip = computed(() => {
  let playoutRef = ''
  vodLinkedPlayout.value.forEach((item, idx) => {
    const isLast = idx === vodLinkedPlayout.value.length - 1
    playoutRef += `<a class="underline" href="/app/tv-playout/${item._id}">${item.name.length > 24 ? item.name.substring(0, 24) + '...' : item.name}${isLast ? '' : ', '}</a> `
  })
  return playoutRef
})

const canEncodeVideos = computed(() => {
  const encodingUnits = _.get(
    store.state.User.baseSub,
    "package.definition.encodingUnits"
  );
  return encodingUnits > 0;
})

const hasAccessToVodReplace = computed(() => {
  const subs = store.state.User.subscriptions || [];
  const active = subs.filter(x => x.enabled && ['vod', 'live'].includes(x.category));
  return active.some((sub) => {
    if (sub.definitionOverride && !_.isNil(sub.definitionOverride.vodReplace)) {
      return sub.definitionOverride.vodReplace;
    } else if (sub.package && !_.isNil(sub.package.definition.vodReplace)) {
      return sub.package.definition.vodReplace;
    } else {
      return false;
    }
  });
});

const emit = defineEmits({
  // falsy payloads are valid; returning the value itself made Vue warn
  'route-change': () => true,
  'video-update': () => true,
  'video-removed': () => true
})

// Kill switch so the recursive polls below stop when the component unmounts
let vodPollAlive = true

const checkVodMoveStatus = async () => {
  if (!vodPollAlive) return;
  const vodFile = props.vodFile;
  const folderKey = props.vodFolder?.key;
  if (!vodFile) {
    setTimeout(checkVodMoveStatus, 5000);
    return;
  }

  try {
    const status = await PlaylistService.vodMoveStatus(folderKey, vodFile.id)
    if (!status) {
      setTimeout(checkVodMoveStatus, 15000);
      isVodBeingMoved.value = true;
    } else {
      isVodBeingMoved.value = false;
    }
  } catch {
    setTimeout(checkVodMoveStatus, 15000);
  }
}

const checkVodRemovable = async () => {
  if (!vodPollAlive) return;
  const vodFile = props.vodFile;
  if (!vodFile) {
    setTimeout(checkVodRemovable, 5000);
    return;
  }

  try {
    const playoutList = await StreamService.getLinkedTvPlayout(props.vodFolder._id, vodFile._id)
    if (!playoutList || _.isEmpty(playoutList)) {
      setTimeout(checkVodRemovable, 15000);
      isVodRemovable.value = true;
    } else {
      isVodRemovable.value = false;
      vodLinkedPlayout.value = playoutList;
    }
  } catch {
    setTimeout(checkVodRemovable, 15000);
  }
}


onMounted(async () => {
  checkVodMoveStatus();
  checkVodRemovable()
})

onBeforeUnmount(() => {
  vodPollAlive = false
  document.removeEventListener('click', onDeleteDocClick)
})

const onCreatePrerecord = () => {
  selectedVodFile.value = _.cloneDeep(props.vodFile)
  selectedVodFile.value.name = selectedVodFile.value.fileName
  selectedVodFile.value.size = bytes(selectedVodFile.value.bytes, false, 2)
  selectedVodFile.value.sizeRaw = selectedVodFile.value.bytes
  selectedVodFile.value.duration = selectedVodFile.value.mediaInfo.duration

  const srcHls = selectedVodFile.value.srcHls
  if (srcHls.includes('/videos/')) selectedVodFile.value.vodId = srcHls.substring(srcHls.indexOf('/videos') + 1, srcHls.indexOf('/index.m3u8'))
  if (srcHls.includes('/episodes/')) selectedVodFile.value.vodId = srcHls.substring(srcHls.indexOf('/episodes') + 1, srcHls.indexOf('/index.m3u8'))
  setTimeout(() => {
    showModal('modal-add-pre-record' + selectedVodFile.value.name);
  }, 100);
}

const clearSelectedVodFile = () => selectedVodFile.value = null

const onClickReplaceVod = () => {
  showModal('confirm-replace-vod')
}

// Opens the shared GlobalUppyUploader (same as VOD "New Video" / pre-recorded upload).
const replaceVideo = () => {
  emitAppEvent(APP_EVENTS.OPEN_VOD_UPLOADER, {
    stream: props.vodFolder,
    replace: {
      videoId: props.vodFile.id,
      oldFileSize: props.vodFile.bytes,
      fileName: props.vodFile.fileName,
      precedence: props.vodFile.precedence,
      currFileId: props.vodFile._id,
    },
  })
}

const showPopover = () => {
  showMoveDialog.value = true
  selectedVideoId.value = props.vodFile?._id || props.vodFile?.refId;
}
const closePopover = () => {
  showMoveDialog.value = false
  selectedVideoId.value = null
}
const openMoveModal = () => {
  selectedVideoId.value = props.vodFile?._id || props.vodFile?.refId;
  showModal('move-vod-modal');
}
const onClickMove = async (newFolder, subfolderId = null) => {
  const videoId = props.vodFile?.id
  // Same folder → only the sub-folder tag changes (visual grouping on the
  // video's playlist meta), no cross-folder move needed.
  if (newFolder === props.vodFolder._id) {
    try {
      await StreamService.setVideoSubfolder(props.vodFolder._id, videoId, subfolderId);
      const target = subfolderId ? vodSubfolders.value.find((s) => s._id === subfolderId)?.name : 'folder root'
      notify({ group: 'success', title: `Moved to ${target || 'sub-folder'}` });
      await store.dispatch('Streams/getFilesInVODFolder');
    } catch (err) {
      console.log(err);
      notify({ group: 'error', title: 'Couldn’t move video', text: err?.message || '' });
    } finally {
      closePopover();
      hideModal('move-vod-modal');
    }
    return
  }
  try {
    const result = await PlaylistService.moveVideoToAnotherFolder(selectedVideoId.value, newFolder, Boolean(props.vodFolder.liveParent));
    if (!result.success) {
      throw new Error("Failed to move the file");
    }
    const dest = vodStreams.value.find((s) => s._id === newFolder)
    notify({ group: "success", text: dest?.name ? `Moving to ${dest.name}…` : "Moving process initiated..." });
    if (dest) finalizeMove(dest, videoId, subfolderId)
    // Back to the folder the video came from (same sub-folder view, if any).
    // Jumping to a sibling video here made it look like this video had been
    // renamed and was still in the old folder.
    const fromSubfolder = props.vodFile?.subfolder
    router.push({
      name: 'VODBucketVideos',
      params: { streamId: props.vodFolder?._id },
      query: fromSubfolder ? { subfolder: fromSubfolder } : {},
    })
  } catch (err) {
    console.log(err);
    notify({ group: "error", text: "Failed to move the file!" });
  } finally {
    closePopover();
    hideModal('move-vod-modal');
  }
}

// A cross-folder move runs asynchronously on the backend. Polls the move status
// (same endpoint as checkVodMoveStatus) until the file has landed, then writes
// the sub-folder tag if one was picked and refreshes folder counts + the list the
// user is looking at. Deliberately not tied to this component's lifetime: the
// page navigates away right after the move is initiated.
const finalizeMove = async (dest, videoId, subfolderId = null) => {
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms))
  const deadline = Date.now() + 3 * 60 * 1000
  while (Date.now() < deadline) {
    try {
      const settled = await PlaylistService.vodMoveStatus(dest.key, videoId)
      if (settled) {
        if (subfolderId) await StreamService.setVideoSubfolder(dest._id, videoId, subfolderId)
        notify({ group: 'success', title: `Video moved to ${dest.name}` })
        store.dispatch('Streams/getStreams')
        store.dispatch('Streams/getFilesInVODFolder')
        return
      }
    } catch {
      // not there yet — keep polling
    }
    await sleep(5000)
  }
  if (subfolderId) {
    notify({ group: 'error', title: `Video moved to ${dest.name}, but it could not be placed in the sub-folder` })
  }
}

const downloadDvrArchive = (url, customFileName = '') => {
  if (url?.includes('archives.liveapi.com')) {
    url = url.replace('archives.liveapi.com', 'recordings.castr.com');
  }
  if (!customFileName) {
    window.location.href = url
  } else {
    if (!customFileName?.includes('.mp4')) {
      customFileName = customFileName + '.mp4';
    }
    url = url + '?filename=' + customFileName;
    isDownloading.value = true;
    const link = document.createElement('a');
    document.body.appendChild(link);
    link.href = url;
    link.target = '_blank';
    link.download = customFileName;
    link.click();
    document.body.removeChild(link);
    isDownloading.value = false;
  }
}

// Inline 2-step delete confirm (no modal) — matches the card delete style.
const confirmingDelete = ref(false)
const deleteConfirmEl = ref(null)
const onDeleteDocClick = (e) => {
  if (deleteConfirmEl.value && !deleteConfirmEl.value.contains(e.target)) cancelDeleteConfirm()
}
const startDeleteConfirm = () => {
  if (!canDeleteVideoFile.value) return
  confirmingDelete.value = true
  // Defer so this same click doesn't immediately trigger the outside handler.
  requestAnimationFrame(() => document.addEventListener('click', onDeleteDocClick))
}
const cancelDeleteConfirm = () => {
  confirmingDelete.value = false
  document.removeEventListener('click', onDeleteDocClick)
}
const confirmDelete = () => {
  document.removeEventListener('click', onDeleteDocClick)
  confirmingDelete.value = false
  removeVideoFile()
}

const removeVideoFile = async () => {
  const video = props.vodFile;
  const videoId = video.id;
  emit('video-update', videoId, { removing: true });

  try {
    // if (video.encodingRequired) {
    if (props.vodFolder.liveParent) {
      const q = {
        streamId: props.vodFolder.liveParent,
        from: video.archive.from,
        duration: video.archive.duration,
        abr: video.archive.abr,
        namedHls: video.archive.namedHls
      };
      await StreamService.deleteStreamDvrArchive(q);
      await store.dispatch('Streams/getStreams');
      notify({
        group: 'success',
        title: "Video deleted",
      });
    } else {
      await StreamService.deleteVodMedia(videoId, props.vodFolder);
      await store.dispatch('Streams/getStreams');
      notify({
        group: 'success',
        title: "Video deleted",
      });
    }
    await store.dispatch('Streams/getFilesInVODFolder');

    track(EVENTS.VIDEO_DELETED, {
      folderId: props.vodFolder?._id,
      fileId: videoId,
      isLiveToVod: !!props.vodFolder?.liveParent,
    });

    const fromSubfolder = video?.subfolder
    router.push({
      name: 'VODBucketVideos',
      params: { streamId: props.vodFolder?._id },
      query: fromSubfolder ? { subfolder: fromSubfolder } : {},
    })
  } catch (e) {
    console.log(e);
    emit('video-update', videoId, { removing: false });
  }
  emit('video-removed', videoId);
}

const showGeneralTagsModal = () => vodTags.value.showManageTagsModal()

const showAddGeneralTagModal = () => {
  showModal('1-modal')
}
</script>

<template>
  <section>
    <!-- Actions: 2x2 grid — Replace / Download / Move / Delete (Figma "Manage video") -->
    <div class="flex flex-col gap-2">
      <div class="grid grid-cols-2 gap-2">
        <Tooltip :active="!hasAccessToVodReplace || vodFolder.liveParent"
          :content="vodFolder.liveParent ? 'Replace feature is not available for Live-to-VOD files' : 'Replace feature is not available for your subscription.'">
          <FormButton type="secondary" fullWidth :disabled="!hasAccessToVodReplace || vodFolder.liveParent"
            @click="onClickReplaceVod">
            <icon-base class="mr-2 shrink-0" fill="transparent">
              <icon-replace />
            </icon-base>
            Replace
          </FormButton>
        </Tooltip>
        <FormButton v-if="canDownloadSource" type="secondary" fullWidth :disabled="isDownloading"
          @click="downloadDvrArchive(vodFolder.liveParent ? vodFile.downloadUrl : 'https://video-assets.castr.com/' + vodFolder.key + '/' + (vodFile.replaceDetails?.videoId || vodFile.id), vodFile.fileName)">
          <icon-base class="mr-2 shrink-0" fill="transparent">
            <icon-download />
          </icon-base>
          {{ isDownloading ? 'Downloading' : 'Download' }}
        </FormButton>
        <Tooltip v-else :active="!canDownloadSource">
          <FormButton type="secondary" fullWidth disabled>
            <icon-base class="mr-2 shrink-0" fill="transparent">
              <icon-download />
            </icon-base>
            Download
          </FormButton>
          <template #content>
            Upgrade to use the download feature. Check details on <a target="_blank" class="text-reset text-underline"
              href="https://castr.io/pricing">pricing page</a>.
          </template>
        </Tooltip>
        <!-- Move to another folder: the modal + onClickMove already live below; this is the trigger -->
        <Tooltip :active="!!vodFolder.liveParent || isVodBeingMoved"
          :content="vodFolder.liveParent ? 'Move feature is not available for Live-to-VOD files' : 'Moving is still in progress. Once complete, new files can be transferred.'">
          <FormButton type="secondary" fullWidth :disabled="!!vodFolder.liveParent || isVodBeingMoved"
            @click="openMoveModal">
            <icon-base class="mr-2 shrink-0" fill="transparent">
              <icon-move-to />
            </icon-base>
            Move
          </FormButton>
        </Tooltip>
        <FormButton v-if="canDeleteVideoFile && !vodLinkedPlayout" type="danger" fullWidth @click="startDeleteConfirm">
          <icon-base class="mr-2 shrink-0" fill="transparent">
            <icon-bin />
          </icon-base>
          Delete
        </FormButton>
        <Tooltip v-else :hide="1000" :active="!!vodLinkedPlayout || !canDeleteVideoFile">
          <FormButton type="danger" fullWidth disabled>
            <icon-base class="mr-2 shrink-0" fill="transparent">
              <icon-bin />
            </icon-base>
            Delete
          </FormButton>
          <template v-if="vodLinkedPlayout" #content>
            This file is currently linked to <span v-html="isLinkedPlayoutTooltip" />TV Playouts. You can safely delete
            it once it has been removed from all associated TV Playouts.
          </template>
          <template v-else-if="!canDeleteVideoFile" #content>
            You don't have permission. Contact the team owner at <a class="text-dark-8 text-small"
                :href="'mailto:' + ownerEmail">{{ ownerEmail }}</a> to gain access.
          </template>
        </Tooltip>
      </div>
      <!-- Inline 2-step delete confirm, full width under the grid -->
      <div v-if="confirmingDelete" ref="deleteConfirmEl"
        class="rounded-md bg-error-transparent ring-1 ring-error-light/30 p-2.5 flex flex-col gap-2">
        <p class="text-tiny text-surface-text-1 leading-snug">Delete this video permanently? This can't be undone.</p>
        <div class="grid grid-cols-2 gap-2">
          <FormButton type="secondary" fullWidth @click="cancelDeleteConfirm">Cancel</FormButton>
          <FormButton type="red" fullWidth @click="confirmDelete">Delete</FormButton>
        </div>
      </div>
    </div>
    <add-pre-record-stream-modal v-if="selectedVodFile" :video="selectedVodFile" :stream="vodFolder"
      @modal-dismiss="clearSelectedVodFile" @modal-confirm="clearSelectedVodFile" />
    <ReplaceVodModal
      body="You are about to replace the video. All changes made in Chapters, Captions, Clips, Schedule, Meta and Thumbnail will be reset except for Embed URL. Are you sure?"
      message="You are going to replace the video" modal-id="confirm-replace-vod" ok-text="Upload New Video"
      cancel-text="Cancel" @modal-confirm="replaceVideo" />
    <ModalBox v-if="!vodFolder.liveParent" v-model="isMoveVodVisible" size="md"
      header-class="!p-3 items-center border-b border-surface-3" wrapper-classes="lg:!h-auto lg:!max-h-none">
      <template #header>
        <h2 class="text-headline font-medium text-white">Move video</h2>
      </template>
      <move-vod :currentFolder="vodFolder._id" :currentSubfolder="vodFile?.subfolder || null" :vodStreams="vodStreams"
        @confirm="onClickMove" @cancel="hideModal('move-vod-modal')" />
    </ModalBox>
  </section>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
